import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/ui/Header';
import Footer from '../components/ui/Footer';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { ArrowRight, Play, X, Phone, Users, Film, ChevronDown } from 'lucide-react';

// Portfolio samples for the editing page (Main Portfolio)
const editingPortfolio = [
  { src: 'https://www.youtube.com/embed/mqLiBAhh800', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/K1BKmnCrZDY', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/Rypk89DQF4M?start=2', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/oxiXNBTLVhw', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/E3JvoqMzwkQ', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/luCntDwXrbQ?start=16', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/-O-exPvWdaQ', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/6NrppyrI3hY', aspect: 'landscape' },
];

// Additional editing videos for "How It Works" section (6 videos)
const editingShowcase = [
  { src: 'https://www.youtube.com/embed/fBkpcKwgPiM?start=21', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/ieExK7MSK1g', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/AVSgELoPQWo', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/wQGPzjSXBA4', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/dbLu5OcBpDw', aspect: 'landscape' },
  { src: 'https://www.youtube.com/embed/3OJBESXVLY0?start=3', aspect: 'landscape' },
];

const getYouTubeId = (src) => {
  const match = src.match(/\/embed\/([^?]+)/);
  return match ? match[1] : null;
};

const faqItems = [
  {
    question: 'What kind of videos can I get edited?',
    answer: 'We can edit any type of video you need! From promotional videos, social media clips, and YouTube content to corporate presentations and event highlights. The only exception is wedding videos — those aren\'t included in our standard packages. If you\'re interested in wedding video editing, please book a call to discuss custom pricing.',
  },
  {
    question: 'How fast is the turnaround?',
    answer: 'We can handle up to 4 shortform videos within a 72-hour period and 1 longform video within a 48–72 hour period. If content is sent in large batches, it may take a bit longer to work through. We always communicate timelines clearly so there are no surprises.',
  },
  {
    question: 'Can I request revisions after the edits are delivered?',
    answer: 'Absolutely! We offer up to 3 rounds of revisions per video. Revisions are typically handled within 24–48 hours, sometimes faster depending on the level of changes needed. That said, after the first 3 months of working together, most clients rarely need more than one round of revisions — and sometimes none at all.',
  },
  {
    question: 'What if I need more videos than my plan includes?',
    answer: 'No problem! Additional videos are billed at the overage rate for your plan. Shortform overage is $65/video and longform overage is $250/video. You can also upgrade your plan at any time.',
  },
];

const Editing = () => {
  const [popupItem, setPopupItem] = useState(null);
  const [openFaq, setOpenFaq] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen relative z-[1]">
      <Header />
      <div className="pt-32 pb-20">

        {/* ===== HERO ===== */}
        <section className="py-16 md:py-24 relative">
          <div className="container mx-auto px-6 text-center">
            <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/50 mb-6">Video Editing</Badge>
            <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-tight">
              Outsourced Editing That<br />
              <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Feels In-House</span>
            </h1>
            <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto mb-4">
              Dedicated U.S.-based editors, fast turnaround, and your branding on every deliverable. Scale without hiring in-house.
            </p>
            <p className="text-sm font-semibold text-amber-400 mb-10">
              🇺🇸 All of our editors are hired within the United States
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg px-10 py-6 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                Book a Discovery Call <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* ===== STATS BAR ===== */}
        <section className="py-12 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-4xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                { number: '1,000+', label: 'Videos Edited' },
                { number: '50+', label: 'Customers Served' },
                { number: '100M+', label: 'Views Generated' },
              ].map((stat, i) => (
                <div key={i} className="text-center bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all">
                  <p className="text-4xl md:text-5xl font-black bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </p>
                  <p className="text-slate-400 font-medium text-lg">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== HOW IT WORKS ===== */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-5xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  Get Your Editor Assigned <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Within 24 Hours</span>
                </h2>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {[
                  { step: '01', title: 'Discovery Call', description: 'Jump on a call with our team so we can find the perfect subscription for you.', icon: <Phone className="w-6 h-6" /> },
                  { step: '02', title: 'Meet Your U.S.-Based Team', description: 'Get introduced to your dedicated American editors who are going to ensure your success.', icon: <Users className="w-6 h-6" /> },
                  { step: '03', title: 'Receive High Quality Edits', description: 'Let the team execute top-level edits for you and your clients — never miss a deadline again.', icon: <Film className="w-6 h-6" /> },
                ].map((item, i) => (
                  <div key={i} className="relative text-center">
                    {i < 2 && (
                      <div className="hidden md:block absolute top-12 left-[60%] w-[80%] h-0.5 bg-gradient-to-r from-amber-500/40 to-amber-500/10 z-0" />
                    )}
                    <div className="relative z-10 bg-slate-900/50 border border-slate-800 rounded-2xl p-8 hover:border-amber-500/30 transition-all h-full">
                      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-amber-500 to-yellow-400 flex items-center justify-center mx-auto mb-5 shadow-lg shadow-amber-500/30">
                        <span className="text-slate-950 font-black text-xl">{item.step}</span>
                      </div>
                      <div className="w-12 h-12 rounded-lg bg-amber-500/10 flex items-center justify-center text-amber-400 mx-auto mb-4">
                        {item.icon}
                      </div>
                      <h3 className="text-white font-bold text-xl mb-3">{item.title}</h3>
                      <p className="text-slate-400">{item.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Video Showcase Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto mt-14">
                {editingShowcase.map((video, index) => {
                  const videoId = getYouTubeId(video.src);
                  const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
                  return (
                    <div key={index} className="cursor-pointer group" onClick={() => setPopupItem(video)}>
                      <div className="relative aspect-video bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                        {thumbnail && (
                          <img src={thumbnail} alt={`Video ${index + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        )}
                        <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                          <div className="w-16 h-16 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                            <Play className="w-8 h-8 text-slate-950 ml-1" />
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* ===== PORTFOLIO ===== */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                Our <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Portfolio</span>
              </h2>
              <p className="text-slate-400 text-lg max-w-xl mx-auto">
                A selection of our recent video editing work.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {editingPortfolio.map((video, index) => {
                const videoId = getYouTubeId(video.src);
                const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : null;
                return (
                  <div
                    key={index}
                    className="cursor-pointer group"
                    onClick={() => setPopupItem(video)}
                  >
                    <div className="relative aspect-video bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                      {thumbnail && (
                        <img
                          src={thumbnail}
                          alt={`Video ${index + 1}`}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                        />
                      )}
                      <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                        <div className="w-16 h-16 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                          <Play className="w-8 h-8 text-slate-950 ml-1" />
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="text-center mt-10">
              <Link to="/portfolio">
                <Button variant="outline" className="border-2 border-amber-500/50 text-amber-400 hover:bg-amber-500/10 hover:border-amber-400 font-bold px-8 py-4 transition-all hover:scale-105">
                  View Full Portfolio <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

        {/* ===== CLIENT TESTIMONIALS ===== */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                Don{`'`}t Take Our <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Word For It</span>
              </h2>
              <p className="text-slate-400 text-lg max-w-xl mx-auto">
                Hear directly from our clients about their experience working with PHOS Studio.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {[
                { src: 'https://www.youtube.com/embed/izRXTVJf0pw', id: 'izRXTVJf0pw' },
                { src: 'https://www.youtube.com/embed/ZXouPQGQOn0', id: 'ZXouPQGQOn0' },
                { src: 'https://www.youtube.com/embed/MHHjat7Zjxs', id: 'MHHjat7Zjxs' },
              ].map((video, index) => (
                <div
                  key={index}
                  className="cursor-pointer group"
                  onClick={() => setPopupItem({ src: video.src, aspect: 'portrait', type: 'youtube' })}
                >
                  <div className="relative aspect-[9/16] bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/50 transition-all hover:shadow-lg hover:shadow-amber-500/10">
                    <img
                      src={`https://img.youtube.com/vi/${video.id}/hqdefault.jpg`}
                      alt={`Client testimonial ${index + 1}`}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                    <div className="absolute inset-0 bg-slate-950/30 group-hover:bg-slate-950/10 transition-all flex items-center justify-center">
                      <div className="w-14 h-14 rounded-full bg-amber-500/90 flex items-center justify-center transform group-hover:scale-110 transition-transform shadow-lg shadow-amber-500/30">
                        <Play className="w-7 h-7 text-slate-950 ml-0.5" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ===== FAQ ===== */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto">
              <div className="text-center mb-14">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-4">
                  Frequently Asked <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Questions</span>
                </h2>
              </div>

              <div className="space-y-4">
                {faqItems.map((faq, index) => (
                  <div
                    key={index}
                    className="bg-slate-900/50 border border-slate-800 rounded-2xl overflow-hidden hover:border-amber-500/30 transition-all"
                  >
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      className="w-full flex items-center justify-between p-6 text-left"
                    >
                      <span className="text-white font-bold text-lg pr-4">{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 text-amber-400 flex-shrink-0 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq === index && (
                      <div className="px-6 pb-6">
                        <p className="text-slate-400 leading-relaxed">{faq.answer}</p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ===== BOTTOM CTA ===== */}
        <section className="py-20 relative">
          <div className="container mx-auto px-6">
            <div className="max-w-3xl mx-auto text-center bg-gradient-to-br from-slate-900/80 to-slate-800/50 border border-amber-500/30 rounded-3xl p-12 md:p-16 shadow-2xl shadow-amber-500/10">
              <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-white mb-6">
                Book a Call <span className="bg-gradient-to-r from-amber-400 to-yellow-300 bg-clip-text text-transparent">Today</span>
              </h2>
              <p className="text-slate-400 text-lg mb-10 max-w-xl mx-auto">
                PHOS Studio was founded with one simple goal — to be your trusted creative partner. Book a call with us today!
              </p>
              <Link to="/contact">
                <Button size="lg" className="bg-gradient-to-r from-amber-500 to-yellow-400 text-slate-950 hover:from-amber-400 hover:to-yellow-300 font-bold text-lg px-10 py-6 shadow-2xl shadow-amber-500/40 hover:shadow-amber-500/60 transition-all hover:scale-105">
                  Book a Discovery Call <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>
          </div>
        </section>

      </div>

      {/* Video Popup */}
      {popupItem && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/90 backdrop-blur-sm p-4"
          onClick={() => setPopupItem(null)}
        >
          <div
            className="relative max-w-4xl w-full bg-slate-900 rounded-2xl border border-amber-500/30 shadow-2xl shadow-amber-500/10 overflow-hidden mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setPopupItem(null)}
              className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-slate-800/80 text-white hover:bg-amber-500/20 hover:text-amber-400 flex items-center justify-center transition-all"
            >
              <X className="w-5 h-5" />
            </button>
            <div className="w-full aspect-video">
              <iframe
                src={popupItem.src}
                className="w-full h-full border-0"
                title="Video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              />
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Editing;
